// =============================================================================
// components/community/notificationPrefs.ts — per-community notification prefs
//
// CONTRACT (backend agent is adding these; coded against, with fallback):
//   GET /servers/:id/notification-preferences → { level:'all'|'mentions'|'none', muted:boolean }
//   PUT /servers/:id/notification-preferences   same body → same shape
//
// GRACEFUL FALLBACK: until the endpoint lands (404 / any failure), prefs are
// persisted locally in AsyncStorage keyed by communityId. Every save ALWAYS
// writes the local copy first (so toggles work offline/today) and then
// best-effort PUTs to the server; once the backend endpoint ships, the same
// save path syncs it with zero client changes. Loads prefer the server copy
// when it answers, else the local copy, else the defaults.
// =============================================================================
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from '../../services/api';

export type NotifLevel = 'all' | 'mentions' | 'none';

export interface CommunityPrefs {
  level: NotifLevel;
  muted: boolean;
}

export const DEFAULT_PREFS: CommunityPrefs = { level: 'all', muted: false };

const KEY_PREFIX = '@cryb/community-prefs:';

function isLevel(v: any): v is NotifLevel {
  return v === 'all' || v === 'mentions' || v === 'none';
}

function normalize(raw: any): CommunityPrefs | null {
  if (!raw || typeof raw !== 'object') return null;
  // Tolerate a nested { preferences: {...} } envelope.
  const node = raw.preferences ?? raw;
  if (!node || typeof node !== 'object') return null;
  if (!isLevel(node.level) && typeof node.muted !== 'boolean') return null;
  return {
    level: isLevel(node.level) ? node.level : DEFAULT_PREFS.level,
    muted: typeof node.muted === 'boolean' ? node.muted : DEFAULT_PREFS.muted,
  };
}

async function readLocal(communityId: string): Promise<CommunityPrefs | null> {
  try {
    const raw = await AsyncStorage.getItem(`${KEY_PREFIX}${communityId}`);
    return raw ? normalize(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

async function writeLocal(communityId: string, prefs: CommunityPrefs): Promise<void> {
  try {
    await AsyncStorage.setItem(`${KEY_PREFIX}${communityId}`, JSON.stringify(prefs));
  } catch {
    /* non-fatal — prefs just won't survive a relaunch */
  }
}

/** Load prefs: server copy when the endpoint answers, else local, else defaults. */
export async function loadCommunityPrefs(communityId: string): Promise<CommunityPrefs> {
  const id = encodeURIComponent(String(communityId || ''));
  if (!id) return DEFAULT_PREFS;
  const res = await api<any>(`/servers/${id}/notification-preferences`);
  if (res.success) {
    const server = normalize(res.data);
    if (server) {
      // Keep the local mirror fresh so a later offline load agrees.
      await writeLocal(communityId, server);
      return server;
    }
  }
  return (await readLocal(communityId)) ?? DEFAULT_PREFS;
}

/**
 * Save prefs. ALWAYS persists locally (works today, endpoint or not), then
 * best-effort PUTs to the backend — when the endpoint lands this same call
 * starts syncing server-side automatically. Never throws.
 */
export async function saveCommunityPrefs(communityId: string, prefs: CommunityPrefs): Promise<void> {
  const id = encodeURIComponent(String(communityId || ''));
  if (!id) return;
  await writeLocal(communityId, prefs);
  try {
    await api(`/servers/${id}/notification-preferences`, {
      method: 'PUT',
      body: JSON.stringify(prefs),
    });
  } catch {
    /* endpoint not live yet / offline — local copy is the source of truth */
  }
}
